/**
 * The video-analysis drawings, transcribed from the canon (`docs/design/analiza-wideo-2026-09-23/`).
 * Two consumers: the Forma tab (`pages/client-detail`) and the clip player (`features/clip-player`).
 * Every glyph is decorative — the control carrying it has its own name — so `aria-hidden` is baked in.
 */

import type { ReactNode } from 'react';

interface GlyphProps {
  readonly className?: string;
}

function Stroke({ className, children }: GlyphProps & { readonly children: ReactNode }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.7}
      viewBox="0 0 24 24"
    >
      {children}
    </svg>
  );
}

/** A filled camera: the exercise has a recording, and a table cell was filmed. */
export function FilmGlyph({ className }: GlyphProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <rect fill="currentColor" height="12" rx="3" width="14" x="2" y="6" />
      <path d="M17.5 10.2l4.5-2.6v8.8l-4.5-2.6z" fill="currentColor" />
    </svg>
  );
}

export function PlayGlyph({ className }: GlyphProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <path d="M7 4.5v15l12-7.5z" fill="currentColor" />
    </svg>
  );
}

export function PauseGlyph({ className }: GlyphProps) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <rect fill="currentColor" height="15" rx="1" width="4" x="6" y="4.5" />
      <rect fill="currentColor" height="15" rx="1" width="4" x="14" y="4.5" />
    </svg>
  );
}

export function FrameBackGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M16 6l-8 6 8 6z" fill="currentColor" stroke="none" />
      <path d="M6 6v12" />
    </Stroke>
  );
}

export function FrameForwardGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M8 6l8 6-8 6z" fill="currentColor" stroke="none" />
      <path d="M18 6v12" />
    </Stroke>
  );
}

export function LoopGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v1a4 4 0 0 1-4 4H3" />
    </Stroke>
  );
}

export function CompareGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <rect height="16" rx="1.5" width="7.5" x="3" y="4" />
      <rect height="16" rx="1.5" width="7.5" x="13.5" y="4" />
    </Stroke>
  );
}

export function ZoomGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
      <path d="M11 8v6M8 11h6" />
    </Stroke>
  );
}

export function TheatreGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <rect height="12" rx="2" width="19" x="2.5" y="6" />
      <path d="M2.5 15h19" />
    </Stroke>
  );
}

export function MutedGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M4 9v6h4l5 4V5L8 9z" />
      <path d="M17 9l5 6M22 9l-5 6" />
    </Stroke>
  );
}

export function SoundGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M4 9v6h4l5 4V5L8 9z" />
      <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />
    </Stroke>
  );
}

export function KeyboardGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <rect height="12" rx="2" width="19" x="2.5" y="6" />
      <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" />
    </Stroke>
  );
}

export function ChevronGlyph({
  className,
  direction,
}: GlyphProps & { readonly direction: 'left' | 'right' | 'up' | 'down' }) {
  const path = {
    left: 'M15 6l-6 6 6 6',
    right: 'M9 6l6 6-6 6',
    up: 'M6 15l6-6 6 6',
    down: 'M6 9l6 6 6-6',
  }[direction];
  return (
    <Stroke className={className}>
      <path d={path} />
    </Stroke>
  );
}


export function PinGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M12 21s-6-5.3-6-10a6 6 0 0 1 12 0c0 4.7-6 10-6 10z" />
      <circle cx="12" cy="11" r="2" />
    </Stroke>
  );
}

/** The side panel's „Przypnij": a push pin, as the FINAL v6 boards draw it. */
export function PushPinGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M9 4h6l-1 6 3 3H7l3-3z" />
      <path d="M12 13v7" />
    </Stroke>
  );
}

export function LinkGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7L11.5 6.8" />
      <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.5-1.5" />
    </Stroke>
  );
}

export function SendGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M5 12h13" />
      <path d="M13 6l6 6-6 6" />
    </Stroke>
  );
}

export function CameraGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <path d="M4 8h3l2-2.5h6L17 8h3v11H4z" />
      <circle cx="12" cy="13" r="3.5" />
    </Stroke>
  );
}

export function ClockGlyph({ className }: GlyphProps) {
  return (
    <Stroke className={className}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Stroke>
  );
}

